import React, { useState, useEffect, useCallback } from "react";
import { AiOutlineArrowLeft, AiOutlineArrowRight } from "react-icons/ai";
import { FaQuoteLeft, FaUser } from "react-icons/fa";
import { motion } from "framer-motion";
import { reviews } from "../shared/reviewsData";
import { Link } from "react-router-dom";

const TestimonialCard = ({ testimonial }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const maxLength = 150;
  const isLongTestimonial = testimonial.testimonial.length > maxLength;

  const getDisplayText = () => {
    if (!isLongTestimonial || isExpanded) {
      return testimonial.testimonial;
    }
    return (
      testimonial.testimonial.substr(
        0,
        testimonial.testimonial.lastIndexOf(" ", maxLength)
      ) + "..."
    );
  };

  return (
    <div className="relative group h-full">
      {/* Card glow effect */}
      <div
        className="absolute inset-0 bg-gradient-to-r from-teal-500/10 to-blue-500/10 
                    rounded-2xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-200"
      />

      {/* Main card container with flex column */}
      <div
        className="relative flex flex-col h-full min-h-[400px] backdrop-blur-xl bg-white/5 p-8 rounded-2xl
                    border border-white/10 group-hover:border-white/20
                    transition-all duration-200 hover:transform hover:scale-[1.02]"
      >
        {/* Content section that can grow/shrink */}
        <div className="flex-grow">
          <FaQuoteLeft className="text-teal-400 text-3xl mb-6" />

          <div className="text-gray-300 text-lg leading-relaxed">
            <p>{getDisplayText()}</p>
            {isLongTestimonial && (
              <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="text-teal-400 hover:text-teal-300 mt-2 text-sm font-medium transition-colors duration-200"
              >
                {isExpanded ? "Show Less" : "Read More"}
              </button>
            )}
          </div>
        </div>

        {/* Footer section - fixed at bottom */}
        <div className="mt-auto pt-6 border-t border-white/10">
          <div className="flex items-center">
            <div
              className="w-12 h-12 rounded-full bg-gradient-to-r from-teal-400 to-blue-400 
                          flex items-center justify-center"
            >
              <FaUser className="text-white text-xl" />
            </div>
            <div className="ml-4 flex-grow">
              <h4 className="text-white font-semibold">{testimonial.name}</h4>
              <p className="text-teal-400 text-sm">{testimonial.location}</p>
            </div>
            <div className="flex gap-1">
              {[...Array(testimonial.rating)].map((_, i) => (
                <span key={i} className="text-amber-400">
                  ★
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const TestimonialsSection = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const sortedReviews = [...reviews].sort((a, b) => Number(b.id) - Number(a.id));
  const itemsPerPage = {
    desktop: 3,
    tablet: 2,
    mobile: 1,
  };

  const getCurrentItemsPerPage = useCallback(() => {
    if (typeof window !== "undefined") {
      if (window.innerWidth >= 1024) return itemsPerPage.desktop;
      if (window.innerWidth >= 768) return itemsPerPage.tablet;
      return itemsPerPage.mobile;
    }
    return itemsPerPage.desktop;
  }, [itemsPerPage.desktop, itemsPerPage.tablet, itemsPerPage.mobile]);

  const [currentItemsPerPage, setCurrentItemsPerPage] = useState(
    getCurrentItemsPerPage()
  );

  useEffect(() => {
    const handleResize = () => {
      setCurrentItemsPerPage(getCurrentItemsPerPage());
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [getCurrentItemsPerPage]);

  const getTotalPages = useCallback(() => {
    return Math.ceil(sortedReviews.length / currentItemsPerPage);
  }, [sortedReviews.length, currentItemsPerPage]);  

  const nextTestimonial = useCallback(() => {
    const maxIndex = getTotalPages() - 1;
    setActiveIndex((prev) => (prev >= maxIndex ? 0 : prev + 1));
  }, [getTotalPages]);

  const prevTestimonial = () => {
    const maxIndex = getTotalPages() - 1;
    setActiveIndex((prev) => (prev <= 0 ? maxIndex : prev - 1));
  };

  useEffect(() => {
    let interval;
    if (!isPaused) {
      interval = setInterval(nextTestimonial, 7000);
    }
    return () => clearInterval(interval);
  }, [isPaused, nextTestimonial]);

  return (
    <section className="py-20 relative overflow-hidden">
      {/* Background with enhanced patterns */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#1a1f35] to-[#111827]">
        <div className="absolute inset-0 opacity-30">
          <div
            className="absolute w-full h-full"
            style={{
              backgroundImage:
                "radial-gradient(circle at 2px 2px, rgba(255,255,255,0.05) 1px, transparent 0)",
              backgroundSize: "32px 32px",
            }}
          />
        </div>
        {/* Animated gradient orbs */}
        <div className="absolute inset-0">
          {[...Array(3)].map((_, i) => (
            <div
              key={i}
              className="absolute rounded-full blur-3xl opacity-20 animate-pulse"
              style={{
                background:
                  i === 0
                    ? "linear-gradient(to right, #0ea5e9, #2dd4bf)"
                    : i === 1
                    ? "linear-gradient(to right, #8b5cf6, #6366f1)"
                    : "linear-gradient(to right, #14b8a6, #0d9488)",
                width: "40%",
                height: "40%",
                top: `${30 * i}%`,
                left: `${25 * i}%`,
                animation: `pulse ${6 + i}s infinite`,
              }}
            />
          ))}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 relative z-10">
        {/* Section header with Read All Reviews link */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center md:text-left"
          >
            <span
              className="inline-block px-4 py-1 mb-4 rounded-full text-teal-400 text-sm font-medium
                          bg-teal-400/10 border border-teal-400/20"
            >
              TESTIMONIALS
            </span>
            <h2
              className="text-4xl md:text-5xl font-bold mb-4 bg-clip-text text-transparent 
                         bg-gradient-to-r from-white to-gray-300"
            >
              Success Stories from Our Clients
            </h2>
            <p className="text-gray-400 text-lg max-w-2xl">
              Join hundreds of satisfied travelers who trusted us with their
              visa journey
            </p>
          </motion.div>

          <Link
            to="/all-reviews"
            className="mt-6 md:mt-0 px-6 py-3 rounded-xl bg-gradient-to-r from-teal-500 to-teal-400
                     text-white font-semibold transition-all duration-300 hover:shadow-lg 
                     hover:shadow-teal-500/25 hover:scale-105 self-center md:self-auto"
          >
            Read All Reviews
          </Link>
        </div>

        {/* Testimonials carousel */}
        <div className="relative">
          {/* Navigation buttons */}
          <button
            onClick={() => {
              prevTestimonial();
              setIsPaused(true);
            }}
            onMouseEnter={() => setIsPaused(true)}
            className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 md:-translate-x-8 z-10
                     bg-white/5 hover:bg-white/10 text-white p-3 rounded-full
                     backdrop-blur-xl transition-all duration-200 border border-white/10
                     hover:border-white/20 hover:shadow-lg hover:shadow-teal-500/10"
          >
            <AiOutlineArrowLeft size={24} />
          </button>

          <button
            onClick={() => {
              nextTestimonial();
              setIsPaused(true);
            }}
            onMouseEnter={() => setIsPaused(true)}
            className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 md:translate-x-8 z-10
                     bg-white/5 hover:bg-white/10 text-white p-3 rounded-full
                     backdrop-blur-xl transition-all duration-200 border border-white/10
                     hover:border-white/20 hover:shadow-lg hover:shadow-teal-500/10"
          >
            <AiOutlineArrowRight size={24} />
          </button>

          {/* Slider with simplified transition */}
          <div className="overflow-hidden">
            <div
              className="flex"
              style={{
                transform: `translateX(-${activeIndex * 100}%)`,
                transition: "transform 300ms ease-out",
              }}
            >
              {sortedReviews.map((testimonial, index) => (
  <div
    key={testimonial.id}
    className={`px-4 ${
      currentItemsPerPage === 3
        ? "w-1/3"
        : currentItemsPerPage === 2
        ? "w-1/2"
        : "w-full"
    }`}
    style={{ flex: "0 0 auto" }}
  >
    <TestimonialCard testimonial={testimonial} />
  </div>
))}

            </div>
          </div>

          {/* Progress indicators */}
          <div className="flex justify-center gap-2 mt-8">
            {[...Array(getTotalPages())].map((_, index) => (
              <button
                key={index}
                onClick={() => {
                  setActiveIndex(index);
                  setIsPaused(true);
                }}
                className="group relative"
              >
                <div
                  className={`h-2 rounded-full transition-all 
                              ${
                                activeIndex === index
                                  ? "w-8 bg-gradient-to-r from-teal-400 to-blue-400"
                                  : "w-2 bg-white/20"
                              }`}
                >
                  <div
                    className="absolute inset-0 bg-gradient-to-r from-teal-400 to-blue-400 
                               rounded-full blur opacity-50 group-hover:opacity-100 transition-opacity"
                  />
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default TestimonialsSection;
